<template>
    <div class="devices-page">
        <div class="support-note" v-if="!loading">
            <span class="support-note__eyebrow">POS Fleet</span>
            <p class="support-note__copy">
                Review tablet app build versions by location. Release a stuck terminal code before re-registering it on another tablet.
            </p>
        </div>
        <div class="release-summary" v-if="!loading">
            <span class="release-summary__label">Latest active release</span>
            <strong>{{ latestActiveReleaseSummary }}</strong>
            <p>Each POS row compares the tablet's reported build against the newest active release targeted to that device.</p>
        </div>
        <CrudIndex
            v-if="!loading"
            :key="tableKey"
            title="Device"
            :viewColumns="columns"
            fetchUrl="/devices"
            createUrl="/devices"
            updateRootUrl="/devices"
            :permission="PERMISSION_CODES.CMS_VIEW_DEVICE"
            deleteRootUrl="/devices"
            :defaultPerPage="20"
            :createFields="createFields"
            onlyBtnIcons
            :reloadOnSuccess="true"
            :getFormatter="formatDevices"
            :processingCustomActionId="processingCustomActionId"
            :processingCustomAction="processingCustomAction"
            customActionTitle="Release"
            :customActionPermission="PERMISSION_CODES.CMS_EDIT_DEVICE"
            @handleCustomAction="handleReleaseAction"
        />
    </div>
</template>

<script>
import { mapState } from "vuex"
import CrudIndex from "../../../components/crud/index.vue"
import { confirmDialog, createField } from "../../../utils/common"
import { PERMISSION_CODES } from "../../../store/permission"
import { http } from "../../../utils/http"

export default {
    data() {
        return {
            loading: true,
            tableKey: 0,
            processingCustomAction: false,
            processingCustomActionId: null,
            releases: [],
            customers: [],
            discountSlabs: [],
            createFields: [],
            columns: [
                {
                    label: "Device",
                    field: "deviceLabel",
                    type: "string",
                    thClass: "devices-col devices-col--device",
                    tdClass: "devices-cell devices-cell--device",
                },
                {
                    label: "Warehouse",
                    field: "warehouseName",
                    type: "string",
                    thClass: "devices-col devices-col--warehouse",
                    tdClass: "devices-cell devices-cell--warehouse",
                },
                {
                    label: "Status",
                    field: "statusSummary",
                    type: "string",
                    thClass: "devices-col devices-col--status",
                    tdClass: "devices-cell devices-cell--status",
                },
                {
                    label: "Binding",
                    field: "bindingSummary",
                    type: "string",
                    thClass: "devices-col devices-col--binding",
                    tdClass: "devices-cell devices-cell--binding",
                },
                {
                    label: "App Build",
                    field: "appBuildSummary",
                    type: "string",
                    thClass: "devices-col devices-col--app-build",
                    tdClass: "devices-cell devices-cell--app-build",
                },
                {
                    label: "Release Status",
                    field: "releaseStatusSummary",
                    type: "string",
                    thClass: "devices-col devices-col--release-status",
                    tdClass: "devices-cell devices-cell--release-status",
                },
                {
                    label: "Updated",
                    field: "lastStatusChangeSummary",
                    type: "string",
                    thClass: "devices-col devices-col--updated",
                    tdClass: "devices-cell devices-cell--updated",
                },
            ],
            PERMISSION_CODES,
        }
    },
    components: { CrudIndex },
    async mounted() {
        await this.loadReleaseContext()
        this.setupFields()
    },
    computed: {
        ...mapState("misc", ["warehouses"]),
        activeReleases() {
            return this.releases
                .filter((release) => release.status === "active")
                .slice()
                .sort((a, b) => Number(b.versionCode || 0) - Number(a.versionCode || 0))
        },
        latestActiveReleaseSummary() {
            const latest = this.activeReleases[0]
            if (!latest) return "No active POS release"
            return `v${latest.versionName} build ${latest.versionCode}`
        },
    },
    methods: {
        async loadReleaseContext() {
            try {
                const res = await http.get("/pos-app/releases")
                const releases = res.data?.result
                this.releases = Array.isArray(releases) ? releases : []
            } catch (err) {
                console.log("error loading POS release context", err)
                this.releases = []
            }
        },
        shortenBinding(value = "") {
            if (!value) return "Unbound"
            if (value.length <= 22) return value
            return `${value.slice(0, 16)}...${value.slice(-4)}`
        },
        formatDevices(items = []) {
            return items.map((item) => {
                return {
                    ...item,
                    deviceLabel: `${item.name || "Unnamed"} (${item.activationCode || "No code"})`,
                    statusSummary: item.statusName || "Inactive",
                    bindingSummary: this.shortenBinding(item.connectedDeviceIdDisplay || ""),
                    appBuildSummary: item.appVersionSummary
                        ? `${item.appVersionSummary}${item.lastAppCheckAtFormatted ? ` · ${item.lastAppCheckAtFormatted}` : ""}`
                        : "Not reported",
                    releaseStatusSummary: this.releaseStatusForDevice(item),
                    lastStatusChangeSummary: item.lastStatusChangeDateFormatted || "No recent change",
                }
            })
        },
        releaseTargetsDevice(release, device) {
            const targets = release.targets || []
            return targets.some((target) => {
                const targetType = String(target.targetType || "")
                const targetId = String(target.targetId || "")
                if (targetType === "device") return targetId === String(device.id)
                if (targetType === "warehouse") return targetId === String(device.warehouseId)
                return false
            })
        },
        latestReleaseForDevice(device) {
            if ((device.deviceType || "").toUpperCase() !== "POS") return null
            return this.activeReleases.find((release) => this.releaseTargetsDevice(release, device)) || null
        },
        releaseStatusForDevice(device) {
            if ((device.deviceType || "").toUpperCase() !== "POS") return "Peripheral"
            const release = this.latestReleaseForDevice(device)
            if (!release) return "No targeted active release"
            const installedBuild = Number(device.appVersionCode || 0)
            const targetBuild = Number(release.versionCode || 0)
            if (!installedBuild) return `Not reported · target ${targetBuild}`
            if (installedBuild >= targetBuild) return `Current · target ${targetBuild}`
            return `Pending ${targetBuild} · installed ${installedBuild}`
        },
        setupFields() {
            if (this.warehouses.length > 0 && !this.createFields.length) {
                this.createFields = this.createFields.concat(
                    createField("Name*", "name", "text", true),
                    createField("Activation Code*", "activationCode", "text", true),
                    createField("Description", "description", "text", false),
                    createField(
                        "Location",
                        "warehouseId",
                        "select",
                        true,
                        "",
                        this.warehouses.map((item) => ({
                            id: item.id,
                            title: item.name,
                        }))
                    ),
                    createField("Device Type", "deviceType", "select", true, "", [
                        {
                            id: "iQPay",
                            title: "iQPay",
                        },
                        {
                            id: "POS",
                            title: "POS",
                        },
                        {
                            id: "Digital Menus",
                            title: "Digital Menus",
                        },
                    ]),
                    createField("Registered Status", "status", "switch", false, true)
                )
                this.loading = false
            }
        },
        async handleReleaseAction(device) {
            if (this.processingCustomAction) return
            const confirmRelease = await confirmDialog({
                title: `Release ${device.name}?`,
                text: `Activation code ${device.activationCode} will be released for registration on another device.`,
                confirmButtonText: "Release device",
            })
            if (!confirmRelease) return

            this.processingCustomAction = true
            this.processingCustomActionId = device.id
            try {
                const res = await http.post(`/devices/${device.id}/release`)
                this.$toast.success(
                    res.data?.message || "Device released successfully."
                )
                this.tableKey += 1
            } catch (err) {
                console.log("error in releasing device", err)
                this.$toast.error(
                    err?.response?.data?.message || "Could not release device."
                )
            }
            this.processingCustomAction = false
            this.processingCustomActionId = null
        },
    },
    watch: {
        warehouses(e) {
            this.setupFields()
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../../assets/variables.scss";

/* Numeric/date alignment in table (handled by CrudIndex/vue-good-table), extra safety */
::v-deep .vgt-table th,
::v-deep .vgt-table td {
  padding: $tableCellPaddingY $tableCellPaddingX !important;
}

.devices-page {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.support-note {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 12px;
  background: rgba($secondaryColor, 0.08);
  color: $textColor;
  border: 1px solid rgba($secondaryColor, 0.18);
  max-width: 820px;
}

.support-note__eyebrow {
  flex: 0 0 auto;
  padding: 4px 8px;
  border-radius: 999px;
  background: rgba($secondaryColor, 0.16);
  color: $primaryColor;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.support-note__copy {
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.4;
}

.release-summary {
  display: grid;
  gap: 3px;
  max-width: 820px;
  padding: 12px 14px;
  border: 1px solid $lightBorderColor;
  border-radius: 10px;
  background: #fff;
}

.release-summary__label {
  color: rgba($textColor, 0.62);
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
}

.release-summary strong {
  color: $primaryColor;
  font-size: 1rem;
}

.release-summary p {
  margin: 0;
  color: rgba($textColor, 0.68);
  font-size: 0.84rem;
}

::v-deep .vgt-inner-wrap {
  border-radius: 14px;
}

::v-deep .vgt-global-search {
  margin-bottom: 8px;
  border-radius: 12px;
  background: rgba($primaryColor, 0.03) !important;
}

::v-deep .vgt-table {
  font-size: 0.95rem !important;
}

::v-deep .vgt-table thead th {
  padding-top: 14px !important;
  padding-bottom: 14px !important;
  font-size: 0.8rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.01em;
  text-transform: uppercase;
  white-space: nowrap;
}

::v-deep .vgt-table tbody tr {
  height: auto !important;
}

::v-deep .vgt-table tbody td {
  padding-top: 14px !important;
  padding-bottom: 14px !important;
  vertical-align: middle !important;
  line-height: 1.45;
}

::v-deep .devices-cell--device {
  min-width: 180px;
  font-weight: 600;
}

::v-deep .devices-cell--warehouse {
  min-width: 125px;
}

::v-deep .devices-cell--status {
  min-width: 90px;
}

::v-deep .devices-cell--binding {
  min-width: 135px;
  font-family: "SFMono-Regular", "Menlo", "Monaco", monospace;
  font-size: 0.8rem;
  color: rgba($textColor, 0.8);
}

::v-deep .devices-cell--updated {
  min-width: 120px;
  color: rgba($textColor, 0.74);
}

::v-deep .devices-cell--release-status {
  min-width: 150px;
  font-weight: 600;
}

::v-deep .crud-actions-column {
  min-width: 132px !important;
  width: 132px !important;
}

::v-deep .action-buttons-container {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: flex-end !important;
  gap: 6px !important;
  flex-wrap: nowrap !important;
  min-width: 0;
}

::v-deep .action-buttons-container .action-btn {
  min-width: 0 !important;
  width: 36px !important;
  height: 32px !important;
  padding: 0 !important;
  border-radius: 8px !important;
  font-size: 0.76rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.01em;
  box-shadow: none !important;
}

::v-deep .action-buttons-container .action-btn i {
  margin-right: 0 !important;
}

::v-deep .action-buttons-container .edit-btn {
  min-width: 36px !important;
}

::v-deep .action-buttons-container .permissions-btn {
  min-width: 36px !important;
}

::v-deep .action-buttons-container .delete-btn {
  min-width: 36px !important;
}

::v-deep .vgt-table td:last-child,
::v-deep .vgt-table th:last-child {
  white-space: nowrap;
}

@media (max-width: 1280px) {
  .support-note {
    flex-direction: column;
  }

  ::v-deep .vgt-table {
    font-size: 0.9rem !important;
  }

  ::v-deep .action-buttons-container {
    gap: 6px !important;
  }

  ::v-deep .action-buttons-container .action-btn {
    padding: 0 10px !important;
  }
}

@media (max-width: 768px) {
  ::v-deep .vgt-table {
    min-width: 320px !important;
  }

  ::v-deep .vgt-table th:nth-child(2),
  ::v-deep .vgt-table th:nth-child(3),
  ::v-deep .vgt-table th:nth-child(4),
  ::v-deep .vgt-table th:nth-child(5),
  ::v-deep .vgt-table th:nth-child(6),
  ::v-deep .vgt-table th:nth-child(8),
  ::v-deep .vgt-table td:nth-child(2),
  ::v-deep .vgt-table td:nth-child(3),
  ::v-deep .vgt-table td:nth-child(4),
  ::v-deep .vgt-table td:nth-child(5),
  ::v-deep .vgt-table td:nth-child(6),
  ::v-deep .vgt-table td:nth-child(8) {
    display: none !important;
  }

  ::v-deep .devices-cell--device {
    min-width: 130px;
  }

  ::v-deep .devices-cell--release-status {
    min-width: 145px;
    white-space: normal !important;
    overflow-wrap: anywhere;
  }

  ::v-deep .crud-actions-column {
    display: none !important;
  }
}
</style>
